import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiError } from '@data-access/api/error';
import { UserOperationDataService, UserOperationMember } from '@data-access/api/user-operation';
import { HeapService } from '@feature/analytics';
import { FreshchatService } from '@feature/freshdesk';
import { BehaviorSubject, EMPTY, from, Observable, of, retry, timer } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AuthLoginResult } from '../../interfaces/login-result.interface';
import { CpUserResponse } from '../../interfaces/user-response.interface';
import { AuthSecureStorageService } from '../secure-storage/auth-secure-storage.service';
import { AuthUsermanagementDataService } from '../usermanagement/usermanagement-data.service';

const RETRY_TIMEOUT: number = 1000;
const RETRY_COUNT: number = 30;
const FINAL_FORBIDDEN_DETAILS: string[] = [
  'login.user.suspended',
  'login.twoFactor.usedOrExpired',
  'login.default.password.expired',
  'login.pin.password.expired',
];

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly usermanagementService: AuthUsermanagementDataService = inject(AuthUsermanagementDataService);
  private readonly userOperationDataService: UserOperationDataService = inject(UserOperationDataService);
  private readonly heapService: HeapService = inject(HeapService);
  private readonly freshchatService: FreshchatService = inject(FreshchatService);
  private readonly secureStorageService: AuthSecureStorageService = inject(AuthSecureStorageService);

  private pendingLoginUser: CpUserResponse | null = null;
  private sessionGeneration: number = 0;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _user$: BehaviorSubject<CpUserResponse | null> = new BehaviorSubject<CpUserResponse | null>(null);
  // TODO: consider this, it is only used in MemberService for the username.
  readonly user$: Observable<CpUserResponse | null> = this._user$
    .asObservable()
    .pipe(filter((user: CpUserResponse | null) => !!user));

  readonly isLoggedIn$: Observable<boolean> = this._user$.pipe(
    tap((user: CpUserResponse | null) => user && this.heapService.identify(user.externalKey)),
    map((user: CpUserResponse | null) => !!user),
  );

  async init(): Promise<void> {
    this.secureStorageService.removeLegacySession();
    this._user$.next(await this.secureStorageService.getUserResponse().catch((): null => null));
  }

  login$(username: string, password: string, token?: string): Observable<AuthLoginResult> {
    return this.usermanagementService.login$(username, password, token).pipe(
      retry({
        count: token ? 0 : RETRY_COUNT,
        delay: (error: HttpErrorResponse) => {
          if (this.userHasNotYetBeenGrantedAccess(error)) {
            return timer(RETRY_TIMEOUT);
          }
          throw error;
        },
      }),
      switchMap((userResponse: CpUserResponse) =>
        userResponse.accessToken ? this.authenticate$(userResponse) : of<AuthLoginResult>({ otpRequired: true }),
      ),
    );
  }

  isLoggedIn(): boolean {
    return !!this._user$.value;
  }

  isLoginPending(): boolean {
    return !!this.pendingLoginUser;
  }

  logout(): void {
    const hadSession: boolean = this.isLoginPending() || this.isLoggedIn();
    this.pendingLoginUser = null;
    this.beginSessionGeneration();
    void this.secureStorageService.clear();
    if (!hadSession) {
      return;
    }
    this._user$.next(null);
    this.heapService.resetIdentify();
    this.freshchatService.clearUser();
  }

  cancelPendingLogin(): boolean {
    if (!this.pendingLoginUser) {
      return false;
    }
    this.pendingLoginUser = null;
    this.beginSessionGeneration();
    void this.secureStorageService.clear();
    return true;
  }

  getToken$(): Observable<string | null> {
    if (this.pendingLoginUser) {
      return of(this.pendingLoginUser.accessToken);
    }
    const user: CpUserResponse | null = this._user$.value;
    if (!user) {
      return of(null);
    }
    const nowInSeconds: number = new Date().getTime() / 1000;
    if (nowInSeconds < user.expirationDate) {
      return of(user.accessToken);
    }
    return this.refreshSession$();
  }

  private authenticate$(userResponse: CpUserResponse): Observable<AuthLoginResult> {
    const generation: number = this.beginSessionGeneration();
    const persisted: Promise<void> = this.secureStorageService
      .setUserResponse(userResponse)
      .catch((error: unknown): never => {
        if (this.isCurrentSession(generation)) {
          this.pendingLoginUser = null;
        }
        throw error;
      });
    this.pendingLoginUser = userResponse;
    return from(persisted).pipe(
      switchMap(() =>
        this.checkIfMemberExists$(userResponse.userDetails.username).pipe(
          catchError(async (): Promise<never> => {
            if (this.isCurrentSession(generation)) {
              this.pendingLoginUser = null;
              await this.secureStorageService.clear();
            }
            throw new ApiError('error.member-not-found');
          }),
        ),
      ),
      filter((): boolean => this.isCurrentSession(generation)),
      map((): AuthLoginResult => {
        this.pendingLoginUser = null;
        this._user$.next(userResponse);
        return { otpRequired: false, user: userResponse };
      }),
      finalize(() => {
        if (this.isCurrentSession(generation)) {
          this.cancelPendingLogin();
        }
      }),
    );
  }

  private beginSessionGeneration(): number {
    return ++this.sessionGeneration;
  }

  private isCurrentSession(generation: number): boolean {
    return generation === this.sessionGeneration;
  }

  private refreshSession$(): Observable<string | null> {
    const generation: number = this.sessionGeneration;
    return this.usermanagementService.refresh$().pipe(
      switchMap((userResponse: CpUserResponse) => this.applyRefreshedSession(userResponse, generation)),
      map((sessionUser: CpUserResponse) => sessionUser.accessToken),
      catchError(() => {
        this.logoutAfterFailedRefresh(generation);
        return EMPTY;
      }),
    );
  }

  private logoutAfterFailedRefresh(generation: number): void {
    if (!this.isCurrentSession(generation)) {
      return;
    }
    console.debug('Could not refresh session, logging out.');
    this.logout();
  }

  private async applyRefreshedSession(userResponse: CpUserResponse, generation: number): Promise<CpUserResponse> {
    this.assertCurrentSession(generation);
    await this.secureStorageService.setUserResponse(userResponse);
    this.assertCurrentSession(generation);
    this._user$.next(userResponse);
    return userResponse;
  }

  private assertCurrentSession(generation: number): void {
    if (!this.isCurrentSession(generation)) {
      throw new Error('The session ended while it was being refreshed.');
    }
  }

  private checkIfMemberExists$(username: string): Observable<boolean> {
    return this.userOperationDataService.getMember$(username).pipe(
      retry({
        count: RETRY_COUNT,
        delay: (error: HttpErrorResponse) => {
          if (this.userHasNotYetBeenGrantedAccess(error)) {
            return timer(RETRY_TIMEOUT);
          }
          throw error;
        },
      }),
      map((member: boolean | UserOperationMember) => !!member),
    );
  }

  /*
   * The user is granted access to the member by askari-connect which is an async process. The
   * error response will be a 403 until the user has been granted access. A login with a one-time
   * code is never retried: the code is consumed by the first attempt.
   */
  private userHasNotYetBeenGrantedAccess(error: HttpErrorResponse | ApiError): boolean {
    const response: HttpErrorResponse | undefined = error instanceof HttpErrorResponse ? error : error.originalError;
    return response?.status === 403 && !FINAL_FORBIDDEN_DETAILS.includes(response.error?.['detail']);
  }
}
